import React, { useState } from "react";
import { Eye, Clock, CheckCircle2, AlertCircle } from "lucide-react";
import { useGetFeedbackQuery } from "../../../../store/apis/helpCenter.api";
import { 
  ConfigurableTable, 
  Button, 
  SearchInput, 
  Badge,
  Select,
  Pagination,
} from "../../../common";
import SimpleTooltip from "../../../common/SimpleTooltip";
import { TableColumn } from "../../../common/Table";
import TicketDetailModal from "../modals/TicketDetailModal";
import FilterWrapper from "../../../common/FilterWrapper";
import { ArticleTableSkeleton } from "../Skeleton";

const ManageTickets: React.FC = () => {
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState<string | undefined>(undefined);
  
  const { data, isLoading, isFetching } = useGetFeedbackQuery({
    page,
    limit,
    search,
    status,
  });

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState<any | null>(null);

  const tickets = (data as any)?.data || [];
  const total = (data as any)?.total || 0;

  const handleView = (ticket: any) => {
    setSelectedTicket(ticket);
    setIsModalOpen(true);
  };

  const columns: TableColumn<any>[] = [
    {
      key: "email",
      title: "USER EMAIL",
      label: "User Email",
      dataIndex: "email",
      required: true,
      isReorderable: false,
      render: (val, record) => <span className="text-sm text-slate-900">{val || record.employeeId || 'Anonymous'}</span>,
    },
    {
      key: "moduleCode",
      title: "MODULE",
      label: "Module",
      dataIndex: "moduleCode",
      required: true,
      isReorderable: false,
      render: (val) => {
        if (!val) return <span className="text-sm text-slate-900">N/A</span>;
        const formatted = String(val)
          .split('_')
          .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
          .join(' ');
        return <span className="text-sm text-slate-900">{formatted}</span>;
      },
    },
    {
      key: "articleTitle",
      title: "ARTICLE",
      label: "Article",
      dataIndex: "articleTitle",
      render: (val) => <span className="text-sm text-slate-900">{val || 'N/A'}</span>,
    },
    {
      key: "description",
      title: "DESCRIPTION",
      label: "Description",
      dataIndex: "description",
      render: (val) => (
        <div className="max-w-[300px] min-w-0">
          <span className="text-sm text-slate-900 block truncate">
            {val as string || "N/A"}
          </span>
        </div>
      ),
    },
    {
      key: "status",
      title: "STATUS",
      label: "Status",
      dataIndex: "status",
      render: (status: string) => {
        const lowerState = (status || "").toLowerCase();
        let variant: "red" | "yellow" | "green" | "gray" = "gray";
        let labelIcon: React.ReactNode = null;
        let label = "Unknown";
        
        if (lowerState === "open") {
          variant = "red";
          label = "OPEN";
          labelIcon = <Clock className="w-3 h-3" />;
        } else if (lowerState === "in_progress" || lowerState === "in-progress") {
          variant = "yellow";
          label = "IN PROGRESS";
          labelIcon = <AlertCircle className="w-3 h-3" />;
        } else if (lowerState === "resolved") {
          variant = "green";
          label = "RESOLVED";
          labelIcon = <CheckCircle2 className="w-3 h-3" />;
        } else if (lowerState === "closed") {
          variant = "gray";
          label = "CLOSED";
          labelIcon = <CheckCircle2 className="w-3 h-3" />;
        } else {
          label = (status || "").toUpperCase();
        }

        return (
          <Badge variant={variant} size="middle">
            <span className="flex items-center gap-1">
              {labelIcon}
              {label}
            </span>
          </Badge>
        );
      },
    },
    {
      key: "createdAt",
      title: "CREATED AT",
      label: "Created At",
      dataIndex: "createdAt",
      render: (val) => {
        if (!val) return <span className="text-sm text-slate-800">N/A</span>;
        const date = new Date(val);
        const d = String(date.getDate()).padStart(2, '0');
        const m = String(date.getMonth() + 1).padStart(2, '0');
        const y = date.getFullYear();
        return <span className="text-sm text-slate-900">{`${d}/${m}/${y}`}</span>;
      },
    },
    {
      key: "actions",
      title: "ACTIONS",
      label: "Actions",
      required: true,
      isReorderable: false,
      render: (_, record) => (
        <div className="flex items-center space-x-2">
          <SimpleTooltip label="View">
            <button
              onClick={(e) => {
                e.stopPropagation();
                e.currentTarget.blur();
                handleView(record);
              }}
              aria-label="View"
              className="text-primary-600 hover:text-primary-800 transition-colors"
            >
              <Eye className="w-4 h-4" aria-hidden="true" />
            </button>
          </SimpleTooltip>
        </div>
      ),
    },
  ];

  const statusOptions = [
    { value: undefined as any, label: "All Status" },
    { value: "OPEN", label: "Open" },
    { value: "CLOSED", label: "Closed" },
  ];

  return (
    <div className="space-y-6">
      <FilterWrapper>
        <div className="flex flex-col md:flex-row items-end gap-4">
          <div className="w-full md:w-96">
            <label className="block text-sm font-semibold text-slate-700 mb-1">Search</label>
            <SearchInput
              value={search}
              onChange={(val) => {
                setSearch(val);
                setPage(1);
              }}
              placeholder="Search by email or module..."
            />
          </div>
          <div className="w-full md:w-64">
            <label className="block text-sm font-semibold text-slate-700 mb-1">Status</label>
            <Select
              value={status}
              onChange={(val) => {
                setStatus(val as string | undefined);
                setPage(1);
              }}
              options={statusOptions}
              placeholder="Filter by Status"
            />
          </div>
        </div>
      </FilterWrapper>

      <div className="bg-white rounded-xl shadow-soft border border-slate-100 overflow-hidden">
        <ConfigurableTable
          columns={columns}
          data={tickets}
          loading={isLoading || isFetching}
          skeleton={<ArticleTableSkeleton />}
          rowKey="_id"
          spacing={0}
          configOptions={{ persistenceKey: "help-center-tickets" }}
          renderColumnSelector={(selector) => (
            <div className="px-6 py-4 border-b border-slate-200 flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
              <div className="flex flex-col">
                <h3 className="text-lg font-bold text-slate-900">User Feedback ({total})</h3>
              </div>
              <div className="flex items-center gap-3 w-full md:w-auto justify-end">
                {selector}
              </div>
            </div>
          )}
        />
      </div>

      <div className="mt-4">
        <Pagination
          currentPage={page}
          totalItems={total}
          itemsPerPage={limit}
          onPageChange={setPage}
          onItemsPerPageChange={(l) => {
            setLimit(l);
            setPage(1);
          }}
        />
      </div>

      {selectedTicket && (
        <TicketDetailModal
          isOpen={isModalOpen}
          onClose={() => setSelectedTicket(null)}
          ticket={selectedTicket}
        />
      )}
    </div>
  );
};

export default ManageTickets;

